import { FC, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import NewsCard from './card/NewsCard'

import banner from '@images/home/banner.jpg'
import banner1 from '@images/home/banner1.png'
import { HeadSection } from 'src/components/elements'
import { NewsModal } from 'src/components/elements/modals'

import 'swiper/css'
import { ChevronNextIcon, ChevronPrevIcon } from '@svgs'
import { Navigation } from 'swiper/modules'
import { useTranslation } from 'react-i18next'
import { useGetNewsQuery } from 'src/redux/api'

interface INewsSection {}

export interface INewsCard {
  title: string
  description: string
  image: string
  fullContent?: string
}

interface IPublishedNews {
  id: string
  slug: string
  title: string
  content: string
  updatedAt: string
}

const CARD_IMAGES = [banner, banner1]
const EXCERPT_LENGTH = 160

const NewsSection: FC<INewsSection> = () => {
  const { t, i18n } = useTranslation()
  const [selectedNews, setSelectedNews] = useState<INewsCard | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // News is managed from the admin panel (CMS); the section disappears
  // when nothing is published for the current language.
  const lang = (i18n.language || 'uk').slice(0, 2)
  const { data } = useGetNewsQuery(lang)

  const newsItems: INewsCard[] = (data?.items ?? []).map((item: IPublishedNews, index: number) => ({
    title: item.title,
    description:
      item.content.length > EXCERPT_LENGTH
        ? `${item.content.slice(0, EXCERPT_LENGTH).trimEnd()}…`
        : item.content,
    image: CARD_IMAGES[index % CARD_IMAGES.length],
    fullContent: item.content,
  }))

  const handleOpenNews = (news: INewsCard) => {
    setSelectedNews(news)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedNews(null)
  }

  if (!newsItems.length) {
    return null
  }

  return (
    <section className="flex flex-col gap-4">
      <HeadSection title={t('news')} />
      <div className="flex gap-4 items-center">
        <button className="md:!flex !hidden icon-button news-swiper-button-prev shrink-0 self-stretch w-4 h-4 fill-ink my-auto hover:fill-brand  disabled:pointer-events-none disabled:opacity-50">
          <ChevronPrevIcon />
        </button>
        <Swiper
          navigation={{
            nextEl: '.news-swiper-button-next',
            prevEl: '.news-swiper-button-prev',
          }}
          className="md:w-full md:flex-1 md:p-0 md:m-0 px-4 -mx-4"
          speed={600}
          slidesPerView={1}
          modules={[Navigation]}
          loop
          centeredSlides
          spaceBetween={8}
          breakpoints={{
            992: {
              slidesPerView: 2,
              spaceBetween: 16,
              loop: false,
              centeredSlides: false,
            },
          }}
        >
          {newsItems.map((item, index) => (
            <SwiperSlide key={index}>
              <NewsCard {...item} onReadMore={() => handleOpenNews(item)} />
            </SwiperSlide>
          ))}
        </Swiper>

        <button className="md:!flex !hidden icon-button news-swiper-button-next shrink-0 self-stretch w-4 h-4 fill-ink my-auto hover:fill-brand disabled:pointer-events-none disabled:opacity-50">
          <ChevronNextIcon />
        </button>
      </div>

      <NewsModal open={isModalOpen} handleClose={handleCloseModal} news={selectedNews} />
    </section>
  )
}

export default NewsSection
